import { useEffect } from 'react';
import { ShieldCheck } from 'lucide-react';
import { API_BASE } from '@/lib/api';

/**
 * Selo oficial BaaS Asaas (Resolução Conjunta BACEN 16/2025).
 *
 * Vai em toda tela da landing onde há dinheiro: o checkout e a proposta
 * pública. O cliente final precisa enxergar, na própria interface, que quem
 * presta o serviço financeiro é o Asaas — a Operax é o canal.
 *
 * O `id` na URL do selo é o do Operax como Tomador, e é ele que o Asaas usa
 * para conferir que o selo foi aplicado.
 */
const SEAL_ID = '2d599a16-35fd-4547-81cc-e3edbe2b08ee';
const SEAL_SRC = `https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Positivo.svg?id=${SEAL_ID}`;
const ASAAS_SITE = 'https://asaas.com';
const ASAAS_PHONE = '0800 009 0037';
const ASAAS_EMAIL = 'contato@asaas.com.br';

/**
 * Evidência de aplicação: uma vez por superfície por sessão, sem bloquear a
 * tela. O endpoint é público porque aqui não existe login.
 */
const logged = new Set<string>();
function useLogDisclosure(surface: string) {
  useEffect(() => {
    if (logged.has(surface)) return;
    logged.add(surface);
    const base =
      typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
        ? ''
        : API_BASE;
    void fetch(`${base}/api/public/compliance/disclosure-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ surface, variant: 'positive' }),
    }).catch(() => {});
  }, [surface]);
}

export function AsaasBaasSeal({
  surface,
  className = '',
  width = 140,
}: {
  /** Superfície financeira (ex.: 'landing_checkout', 'landing_checkout_pix'). */
  surface: string;
  className?: string;
  width?: number;
}) {
  useLogDisclosure(surface);
  return (
    <div className={`flex flex-col items-center gap-2 text-center ${className}`}>
      {/* A garantia de segurança vem antes do selo: é o que a pessoa procura
          no instante de pagar. O selo responde de quem é o serviço financeiro. */}
      <p className="flex items-center gap-1.5 text-[11px] text-[var(--muted)]">
        <ShieldCheck className="h-3.5 w-3.5" style={{ color: 'var(--ok)' }} aria-hidden />
        Pagamento seguro
      </p>
      <a href={ASAAS_SITE} target="_blank" rel="noopener noreferrer" title="Serviços financeiros Asaas">
        {/* Sem `loading="lazy"` de propósito: é disclosure legal, não
            ilustração. Com lazy o navegador adia a busca e o selo não aparece
            na captura de evidência, que foi o que quebrou os prints de julho. */}
        <img
          src={SEAL_SRC}
          alt="Serviços financeiros Asaas"
          width={width}
          height={Math.round((width * 48) / 160)}
          decoding="async"
        />
      </a>
      <p className="max-w-[300px] text-[11px] leading-snug text-[var(--muted)]">
        Serviços financeiros prestados pelo Asaas. Dúvidas: {ASAAS_PHONE} ou {ASAAS_EMAIL}
      </p>
    </div>
  );
}
